import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, Dumbbell, MessageSquare, Settings, Trophy, Timer, ChevronRight } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { resolveTabPath } from '@/lib/tabRoutes';

const BOTTOM_TABS = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Home' },
  { to: '/workout', icon: Dumbbell, label: 'Program' },
  { to: '/chat', icon: MessageSquare, label: 'Coach', hasBadge: true, isPro: false },
  { to: '/achievements', icon: Trophy, label: 'Awards' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

const formatElapsed = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

export function BottomNav() {
  const { api, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  // null = not checked yet, true/false = DB answer. Self-heals a stale banner.
  const [hasLiveSession, setHasLiveSession] = useState(null);

  const isDevUser = user?.is_dev || user?.role === 'admin';

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await api.get('/coach/messages/count');
        setUnreadCount(res.data.unread_count || 0);
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [api]);

  // Clear badge when user is on the Coach page
  useEffect(() => {
    if (location.pathname === '/chat' && unreadCount > 0) {
      setUnreadCount(0);
      api.post('/coach/messages/mark-read').catch(() => {});
    }
  }, [location.pathname, unreadCount, api]);

  // Verify the stored resume banner against the server and drop stale state.
  useEffect(() => {
    if (!localStorage.getItem('bm_active_workout')) { setHasLiveSession(null); return; }
    let active = true;
    api.get('/workout-sessions', { params: { status: 'in_progress', limit: 1 } })
      .then(res => {
        if (!active) return;
        const live = (res.data?.sessions || []).length > 0;
        setHasLiveSession(live);
        if (!live) localStorage.removeItem('bm_active_workout');
      })
      .catch(() => {});
    return () => { active = false; };
  }, [api, location.pathname]);

  // Live elapsed clock for the resume banner
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [swipeX, setSwipeX] = useState(0);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const dragStartRef = useRef(null);
  const dragMovedRef = useRef(false);

  useEffect(() => {
    setConfirmDiscard(false);
    setSwipeX(0);
    if (!localStorage.getItem('bm_active_workout')) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [location.pathname]);

  if (location.pathname === '/subscribe') return null;
  const isOnActiveWorkout = location.pathname.startsWith('/workout/active');
  const activeWorkoutRaw = localStorage.getItem('bm_active_workout');
  let activeWorkout = null;
  if (activeWorkoutRaw && !isOnActiveWorkout) {
    try {
      const parsed = JSON.parse(activeWorkoutRaw);
      if (parsed.path && Date.now() - parsed.startedAt < 3 * 60 * 60 * 1000) {
        activeWorkout = parsed;
      } else {
        localStorage.removeItem('bm_active_workout');
      }
    } catch {
      localStorage.removeItem('bm_active_workout');
    }
  }
  // The banner is only real if the server still has an in-progress session.
  if (activeWorkout && hasLiveSession === false) activeWorkout = null;

  const handleDiscardWorkout = async () => {
    if (!activeWorkout || discarding) return;
    const { planId, dayNumber } = activeWorkout;
    setDiscarding(true);
    let ok = true;
    try {
      await api.post('/workout-sessions/discard', { workout_plan_id: planId, day_number: dayNumber });
    } catch {
      ok = false;
    }
    setDiscarding(false);
    if (!ok) {
      toast.error("Couldn't discard that workout", { description: 'Check your connection and try again.' });
      return;
    }
    try {
      localStorage.removeItem('bm_active_workout');
      localStorage.removeItem(`bm_active_ex_${planId}_${dayNumber}`);
      sessionStorage.removeItem(`workout_exercise_idx_${planId}_${dayNumber}`);
    } catch {}
    setConfirmDiscard(false);
    setSwipeX(0);
    setHasLiveSession(false);
    toast('Workout discarded', {
      description: `Day ${dayNumber} is back to the start. No sets, cardio or stats were saved. Other days are untouched.`,
      duration: 6000,
    });
  };

  const handleProTabTap = (e) => {
    e.preventDefault();
    toast('Coach is coming soon to Pro', { duration: 1500 });
  };

  return (
    <>
      {/* Active workout resume banner */}
      {activeWorkout && (
        <div
          className="fixed left-0 right-0 z-[110] px-3"
          style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom, 0px) + 8px)' }}
        >
          <div className="relative">
          <button
            onClick={() => { if (dragMovedRef.current) return; navigate(activeWorkout.path); }}
            onPointerDown={(e) => { dragStartRef.current = e.clientX; dragMovedRef.current = false; }}
            onPointerMove={(e) => {
              if (dragStartRef.current === null || confirmDiscard) return;
              const dx = e.clientX - dragStartRef.current;
              if (Math.abs(dx) > 8) dragMovedRef.current = true;
              setSwipeX(Math.max(-96, Math.min(96, dx)));
            }}
            onPointerUp={() => {
              if (Math.abs(swipeX) > 56) setConfirmDiscard(true);
              dragStartRef.current = null;
              setSwipeX(0);
            }}
            onPointerCancel={() => { dragStartRef.current = null; setSwipeX(0); }}
            className="relative flex w-full items-center gap-3 overflow-hidden rounded-2xl border border-white/[0.07] bg-[#141416] pl-4 pr-3.5 py-2.5 text-left touch-pan-y active:scale-[0.98]"
            style={{
              boxShadow: '0 10px 30px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)',
              transform: `translateX(${swipeX}px)`,
              transition: swipeX === 0 ? 'transform 180ms ease-out' : 'none',
              opacity: confirmDiscard ? 0 : 1,
            }}
            data-testid="resume-workout-banner"
            aria-label="Workout in progress, tap to resume, swipe to discard"
          >
            {/* Accent spine */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-0 top-0 bottom-0 w-[7px] rounded-r-md bg-accent"
              style={{ boxShadow: '0 0 16px rgba(var(--accent-rgb),0.55)' }}
            />

            {/* Dumbbell + stopwatch mark */}
            <span className="relative shrink-0 text-accent" aria-hidden="true">
              <Dumbbell size={26} strokeWidth={1.9} />
              <Timer
                size={14}
                strokeWidth={2.2}
                className="absolute -bottom-1 -right-1 rounded-full bg-[#141416]"
              />
            </span>

            <span className="min-w-0 flex-1">
              <span className="block truncate font-heading text-[15px] font-black uppercase leading-tight tracking-tight text-white">
                Workout in progress
              </span>
              <span className="mt-0.5 block truncate text-xs text-white/45" data-testid="resume-workout-elapsed">
                Tap to resume
                {activeWorkout.startedAt ? ` · ${formatElapsed(nowTick - activeWorkout.startedAt)} elapsed` : ''}
              </span>
            </span>

            <span className="flex shrink-0 items-center gap-1">
              <span
                className="font-heading text-sm font-black uppercase leading-none tracking-tight text-accent"
                data-testid="resume-workout-day"
              >
                Day {activeWorkout.dayNumber}
              </span>
              <ChevronRight size={18} className="text-white/30" aria-hidden="true" />
            </span>
          </button>

          {confirmDiscard && (
            <div
              className="absolute inset-0 flex items-center gap-2 rounded-2xl border border-red-500/35 bg-[#1A1012] pl-4 pr-2.5"
              style={{ boxShadow: '0 10px 30px rgba(0,0,0,0.55)' }}
              data-testid="resume-workout-discard-confirm"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate font-heading text-[13px] font-black uppercase leading-tight tracking-tight text-white">
                  Discard workout?
                </span>
                <span className="mt-0.5 block truncate text-[11px] text-white/45">
                  Nothing is saved
                </span>
              </span>
              <button
                onClick={() => { setConfirmDiscard(false); setSwipeX(0); }}
                className="shrink-0 rounded-lg px-3 py-2 font-heading text-[11px] font-bold uppercase tracking-wider text-white/60 active:scale-95 transition-transform"
                data-testid="resume-workout-keep-btn"
              >
                Keep
              </button>
              <button
                onClick={handleDiscardWorkout}
                disabled={discarding}
                className="shrink-0 rounded-lg bg-red-500/90 px-3 py-2 font-heading text-[11px] font-bold uppercase tracking-wider text-white active:scale-95 transition-transform disabled:opacity-60"
                data-testid="resume-workout-discard-btn"
              >
                {discarding ? 'Discarding' : 'Discard'}
              </button>
            </div>
          )}
          </div>
        </div>
      )}

      <nav
        className="fixed bottom-0 left-0 right-0 z-[110] border-t backdrop-blur-xl"
        style={{ background: 'rgba(10,10,10,0.92)', borderColor: 'rgba(255,255,255,0.08)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        data-testid="bottom-nav"
      >
        <div className="flex items-center justify-around h-16 max-w-lg mx-auto">
          {BOTTOM_TABS.map(tab => {
            const isActive = resolveTabPath(location.pathname) === tab.to;
            const isLocked = tab.isPro && !isDevUser;

            if (isLocked) {
              return (
                <button
                  key={tab.to}
                  onClick={handleProTabTap}
                  className="relative flex flex-col items-center justify-center w-16 h-14 text-[#555] active:scale-90 transition-transform duration-100"
                  data-testid={`bottom-nav-${tab.label.toLowerCase()}`}
                >
                  <tab.icon size={24} strokeWidth={1.8} />
                  <span className="text-[10px] mt-1 font-heading font-bold">{tab.label}</span>
                  <span className="absolute -top-1 right-0.5 bg-accent text-accent-foreground text-[7px] font-black rounded-full px-1 py-px leading-tight tracking-wider">
                    PRO
                  </span>
                </button>
              );
            }

            return (
              <Link
                key={tab.to}
                to={tab.to}
                className={`relative flex flex-col items-center justify-center w-16 h-14 transition-transform duration-100 active:scale-90 ${
                  isActive ? 'text-accent' : 'text-[#666]'
                }`}
                data-testid={`bottom-nav-${tab.label.toLowerCase()}`}
              >
                {isActive && (
                  <span className="absolute top-0 w-10 h-[3px] rounded-full bg-accent" />
                )}
                <tab.icon size={24} strokeWidth={isActive ? 2.5 : 1.8} fill={isActive ? 'currentColor' : 'none'} />
                <span className={`text-[10px] mt-1 font-heading font-bold ${isActive ? 'text-accent' : ''}`}>
                  {tab.label}
                </span>
                {tab.hasBadge && unreadCount > 0 && !isLocked && (
                  <span className="absolute top-0 right-0.5 bg-accent text-accent-foreground text-[10px] font-black rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}
